<script setup>
import { inject, watch, ref, reactive, computed, onBeforeMount } from 'vue'
import gql from 'graphql-tag'
import { useAnalyzer } from '#/analyzer/useAnalyzer'
import AppDialog from '#/general/AppDialog'
import { useToast } from 'vue-toast-notification'
import { getRawFormData, validateForm, resetForm } from 'vue3-form'
import SelectableFields from 'components/general/SelectableFields'
import AppButton from '#/general/AppButton'
import { useB2CAnalyzer } from 'src/composables/useB2CAnalyzer'
import AppCheckbox from 'components/general/AppCheckbox'
import BackButton from 'components/general/BackButton'
import { useGlobalStore } from 'src/composables/useGlobalStore'
import { useGA } from '#/useGA'
import { useShared } from '#/useShared'
import { useHubspot } from 'src/composables/useHubspot'

const $toast = useToast({ position: 'bottom' })
const { sharedApolloClient, selectedPotential, showMailNotificationPopup, searchText } = useAnalyzer()
const { global, pages, screen, partner, showB2CQuestionnairePopup, analytics, application, isProduction } = inject('Shared')
const { trackingLink } = useGlobalStore()
const { form, showB2cMaxRequestPopup } = useB2CAnalyzer()
const { event } = useGA()
const { interpretErrorMessage } = useShared()
const { identify } = useHubspot()

const currentStep = ref('USER_TYPE')
const checkboxAccepted = ref(false)

async function attemptSubmit () {
  try {
    if (!validateForm(form) || !nextAvailable.value) {
      $toast.open({ type: 'info', message: 'Bitte füllen Sie alle Pflichtfelder aus.' })
      return
    }

    if (!checkboxAccepted.value) {
      $toast.open({ type: 'info', message: 'Bitte lesen und akzeptieren Sie unsere AGB und Datenschutzerklärung, bevor Sie weiterfahren.' })
      return
    }
    const res = await sharedApolloClient.value.query({
      query: gql`
        query ($requestB2CDetailReportInput: RequestB2CDetailReportInputType!) {
          requestB2CDetailsReport(requestB2CDetailReportInput: $requestB2CDetailReportInput) {
            b2cLeadId
            b2cUserId
            success
            error
            openReport
          }
        }
      `,
      variables: {
        requestB2CDetailReportInput: {
          ...getRawFormData(form),
          potentialId: selectedPotential.value._id,
          address: selectedPotential.value.buildings[0].address,
          trackingLink: trackingLink.value,
          isbuildingzonebased: selectedPotential.value.analysis.isBuildingZoneBased,
          parcel_area: selectedPotential.value.parcel.area,
          potential: (selectedPotential.value.analysis.potentialVolume / (selectedPotential.value.parcel.currentVolume + selectedPotential.value.analysis.potentialVolume)) * 100,
          building_class: selectedPotential.value.buildings[0].buildingCategory || '',
          building_zone: selectedPotential.value.buildingZones.filter(e => e.buildingZone && e.buildingZone.shortName).map(e => e.buildingZone.shortName).join(', ') || '',
          referrer: document.referrer,
          year_of_construction: selectedPotential.value.yearOfConstructionEndMax
        }
      },
      fetchPolicy: 'no-cache'
    })

    if (res.data && res.data.requestB2CDetailsReport && res.data.requestB2CDetailsReport.success) {
      trackingLink.value = null
      localStorage.removeItem('trackingLink')
      if (res.data.requestB2CDetailsReport.b2cLeadId && res.data.requestB2CDetailsReport.openReport) {
        window.open(`/details-report?b2cLeadId=${res.data.requestB2CDetailsReport.b2cLeadId}`, '_blank').focus()
      } else {
        showMailNotificationPopup.value = true
      }
      event('b2c_conversion_1', { eventCategory: 'B2C', eventLabel: 'Questionnaire Submitted' })
      identify({
        id: res.data.requestB2CDetailsReport.b2cUserId,
        email: form.value.fields.email.value,
        firstname: form.value.fields.firstName.value,
        lastname: form.value.fields.lastName.value,
        phone: form.value.fields.phoneNumber.value,
        ...(form.value.fields.ownership.value === 'owner' ? { relation_to_property: form.value.fields.ownership.value } : {}),
        web_form_type: 'Analyzer',
        user_persona: 'B2C',
        pain_point_b2c: form.value.fields.painPoints.value.join(';'),
        pain_point_b2c___other___details: form.value.fields.painPointOther.value,
        rp_source_url: document.referrer
      }, '/b2c/analyzer/')
    } else {
      if (res.data.requestB2CDetailsReport.error === 'FREE_DETAILS_REPORT_LIMIT_REACHED') {
        showB2cMaxRequestPopup.value = true
      } else {
        $toast.open({ type: 'warning', message: 'Leider haben wir ein Serverproblem auf unserer Seite festgestellt. Bitte kontaktiere uns auf hallo@raumpioniere.io, damit wir dieses rasch beheben können.' })
      }
    }
    showB2CQuestionnairePopup.value = false
    searchText.value = null
  } catch (e) {
    searchText.value = null
    $toast.open({ type: 'warning', message: interpretErrorMessage(e) })
    console.log(e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}

watch(showB2CQuestionnairePopup, v => {
  if (v) {
    currentStep.value = 'USER_TYPE'
    checkboxAccepted.value = false
    resetForm(form)
  }
})

onBeforeMount(() => {
  resetForm(form)
})

function goNext () {
  switch (currentStep.value) {
    case 'USER_TYPE': {
      form.value.fields.painPoints.value = []
      form.value.fields.painPointOther.value = null
      form.value.fields.ownership.value = null
      switch (form.value.fields.userType.value) {
        case 'QUESTION' : currentStep.value = 'PAIN_POINT'; break
        case 'PROFESSIONAL' : currentStep.value = 'B2B'; break
        case 'CURIOUS' : currentStep.value = 'OWNERSHIP'; break
        case 'INFORMATION' : currentStep.value = 'CONTACT'; form.value.fields.ownership.value = 'non-owner'; break
      }
      break
    }
    case 'OWNERSHIP': {
      currentStep.value = 'CONTACT'
      break
    }
    case 'PAIN_POINT': {
      if (form.value.fields.painPoints.value.includes('other') && !form.value.fields.painPointOther.value) {
        $toast.open({ type: 'info', message: 'Geben Sie uns bitte ein paar Stichworte zu Ihrem Interesse an!' })
        return
      }
      if (form.value.fields.painPoints.value.length > 0) {
        form.value.fields.ownership.value = 'owner'
        currentStep.value = 'CONTACT'
      }
      break
    }
    case 'B2B': {
      form.value.fields.ownership.value = 'non-owner'
      currentStep.value = 'CONTACT'
      break
    }
    case 'CONTACT': {
      if (validateForm(form)) {
        attemptSubmit()
      } else {
        $toast.open({ type: 'info', message: 'Bitte füllen Sie alle Pflichtfelder aus.' })
      }
    }
  }
}

function goBack () {
  switch (currentStep.value) {
    case 'CONTACT': {
      switch (form.value.fields.userType.value) {
        case 'QUESTION' : currentStep.value = 'PAIN_POINT'; break
        case 'PROFESSIONAL' : currentStep.value = 'B2B'; break
        case 'CURIOUS' : currentStep.value = 'OWNERSHIP'; break
        case 'INFORMATION' : currentStep.value = 'USER_TYPE'; break
      }
      break
    }
    case 'OWNERSHIP': {
      currentStep.value = 'USER_TYPE'
      break
    }
    case 'PAIN_POINT': {
      if (!form.value.fields.painPoints.errors) {
        if (form.value.fields.painPoints.value.includes('other') && !form.value.fields.painPointOther.value) {
          $toast.open({ type: 'info', message: 'Bitte füllen Sie alle Pflichtfelder aus.' })
          break
        }
        currentStep.value = 'USER_TYPE'
      } else {
        $toast.open({ type: 'info', message: 'Bitte füllen Sie alle Pflichtfelder aus.' })
      }
      break
    }
    case 'B2B': {
      currentStep.value = 'USER_TYPE'
      break
    }
  }
}

const formSteps = reactive(
  [
    {
      page: 'USER_TYPE',
      title: 'Um eine passgenaue Analyse zu bieten, erlauben wir uns,  Ihnen vorab eine Frage zu stellen:',
      titleTwo: 'Was erhoffen Sie sich von dieser Potenzialanalyse?',
      stepCounter: 1,
      options: [
        { label: 'Antworten auf konkrete Fragen zur Zukunft meiner Liegenschaft', propertyName: 'userType', value: 'QUESTION', type: 'CHECKBOX' },
        { label: 'Relevante Informationen für den Kauf dieser Liegenschaft', propertyName: 'userType', value: 'INFORMATION', type: 'CHECKBOX' },
        { label: 'Reines Interesse', propertyName: 'userType', value: 'CURIOUS', type: 'CHECKBOX' },
        { label: 'Ich bin Immobilienprofi und kann mit Begriffen wie AZ, BMZ oder FH umgehen', propertyName: 'userType', value: 'PROFESSIONAL', type: 'CHECKBOX' }
      ]
    },
    {
      page: 'OWNERSHIP',
      stepCounter: 2,
      options: [
        { label: 'Ich bin Eigentümer dieser Liegenschaft', value: 'owner', propertyName: 'ownership', type: 'CHECKBOX' },
        { label: 'Ich bin nicht Eigentümer dieser Liegenschaft', value: 'non-owner', propertyName: 'ownership', type: 'CHECKBOX' }
      ]
    },
    {
      page: 'PAIN_POINT',
      title: 'Konkret interessiert mich:',
      multiple: true,
      stepCounter: 2,
      options: [
        { label: 'Sanierung und/oder Erweiterung der Liegenschaft', value: 'renew and/or expand', propertyName: 'painPoints', type: 'CHECKBOX' },
        { label: 'Ermittlung des wahren Werts der Liegenschaft', value: 'evaluate', propertyName: 'painPoints', type: 'CHECKBOX' },
        { label: 'Vertiefte Prüfung für den Verkauf der Liegenschaft', value: 'sell', propertyName: 'painPoints', type: 'CHECKBOX' },
        { label: 'Andere', value: 'other', propertyName: 'painPoints', valueName: 'painPointOther', inputLabel: 'Hier kurz Antwort formulieren', type: 'HIDDEN_TEXT_FIELD' }
      ]
    },
    {
      page: 'B2B',
      stepCounter: 2
    },
    {
      page: 'CONTACT',
      mainTitle: 'Kontaktangaben',
      subtitle: 'Gerne geben wir Ihnen eine detaillierte Auskunft zur Liegenschaft.  Bitte ergänzen Sie dieses Formular, damit wir auch wissen, an wen wir uns im Bedarfsfall wenden dürfen. Danke.',
      stepCounter: 3,
      options: [
        { label: 'Vorname', propertyName: 'firstName', name: 'First name', inputType: 'text', type: 'INPUT', rules: ['firstName'] },
        { label: 'Name', propertyName: 'lastName', name: 'Family Name', inputType: 'text', type: 'INPUT', rules: ['lastName'] },
        { label: 'E-Mail', propertyName: 'email', name: 'Email', inputType: 'text', type: 'INPUT', rules: ['required'] },
        { label: 'Telefonnummer', inputLabel: 'Type your question here', propertyName: 'phoneNumber', name: 'Phone number', inputType: 'text', type: 'PHONE', required: false, rules: ['required', 'phoneNumber'] }
      ]
    }

  ]
)

const fullStep = computed(() => formSteps.find(e => e.page === currentStep.value))

const nextAvailable = computed(() => {
  switch (currentStep.value) {
    case 'USER_TYPE': return form.value.fields.userType.value
    case 'OWNERSHIP': return Boolean(form.value.fields.ownership.value)
    case 'PAIN_POINT': {
      if (form.value.fields.painPoints.value.includes('other')) {
        return !!form.value.fields.painPointOther.value
      }
      return form.value.fields.painPoints.value.length > 0
    }
    case 'CONTACT': {
      return !(!form.value.fields.firstName.value ||
        !form.value.fields.lastName.value ||
        !form.value.fields.email.value)
    }
  }
  return false
})

function openLink (url) {
  window.open(url, '_blank').focus()
}
</script>

<template lang="pug">
app-dialog(v-model:show-modal='showB2CQuestionnairePopup' persistent position="center" full-screen maximized :fixed="true" filter-color="rgba(255, 255, 255, 0.5)")
  template(#modal-content)
    .b2cQuestionnairePopupContainer
      template(v-if="currentStep !== 'B2B'")
        .close-btn(@click="showB2CQuestionnairePopup = false")
          svg(width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg')
            path(d='M14 1.41L12.59 0L7 5.59L1.41 0L0 1.41L5.59 7L0 12.59L1.41 14L7 8.41L12.59 14L14 12.59L8.41 7L14 1.41Z' fill='#626C85')
        .steps
          .step.firstStep.activeStep
          .step(:class="`${fullStep.stepCounter > 1 ? 'activeStep' : 'inactiveStep'}`" )
          .step.lastStep(:class="`${fullStep.stepCounter > 2 ? 'activeStep' : 'inactiveStep'}`" )
      .contentContainer
        template(v-if="currentStep !== 'B2B'")
          .mainTitle {{ fullStep.mainTitle }}
          .title {{ fullStep.title }}
          .title.titleTwo(v-if="fullStep.titleTwo") {{ fullStep.titleTwo }}
          .subtitle {{ fullStep.subtitle }}
          form(@submit.prevent="attemptSubmit").b2cQuestionnaireForm
            template(v-for="formStep in formSteps" :key="formStep.title")
              selectable-fields(
                v-if="currentStep === formStep.page"
                v-model:form="form"
                :options="formStep.options"
                :multiple="formStep.multiple"
              )
          template(v-if="currentStep === 'CONTACT'")
            app-checkbox(v-model:value="checkboxAccepted" :option-value="true" :boolean="true")
              .checkboxTitle
                .checkboxText Ich akzeptiere die Raumpioniere
                .checkboxLink(@click="openLink('https://raumpioniere.ch/terms-and-conditions')") AGB
                .checkboxText und
                .checkboxLink(@click="openLink('https://raumpioniere.ch/privacy-policy')") Datenschutzerklärung
            .submitContainer
              app-button.subtmitBtn(
                label="Kontaktangaben bestätigen"
                padding="0 26px"
                bg-color="#5FEFC0"
                label-color="#22223C"
                bg-hover="#5FEFC0"
                @clicked="goNext"
                :disabled="!nextAvailable"
              )
        template(v-else)
          .step5--title Vielen Dank für Ihr Interesse an der Potenzialanalyse.
          .step5--subtitle
            span Auf unserer
            span.step5--subtitle-green(@click="openLink('/solutions')") B2B-Seite
            span finden Sie Inspiration für Ihre Neukunden-Akquise. Hier zeigen wir Ihnen gerne den Report für Privatpersonen.
          app-button.step5--btn(@clicked="goNext()" label="Verstanden - Weiter zum Report" bg-color="#5fefc0" label-color="#22223C" bg-hover="#5FEFC0")
        .btnsContainer
          back-button(v-if="currentStep !== 'USER_TYPE'" @click="goBack()")
          div(v-else)
          app-button.next-btn(@clicked="goNext()" :disabled="!nextAvailable" v-if="currentStep !== 'CONTACT'" label="Weiter" bg-color="#5fefc0" label-color="#22223C" bg-hover="#5FEFC0")
</template>

<style lang="sass">
.b2cQuestionnairePopupContainer
  position: relative
  width: 572px
  background: linear-gradient(152.99deg, #2F3849 78.27%, #21252E 157.29%)
  box-shadow: 0px 15px 30px rgba(166, 166, 166, 0.25)
  border-radius: 10px
  max-width: 100vw
  @media screen and (min-width: 1024px)
    margin-top: 90px
    padding: 20px 40px
  @media screen and (max-width: 1023px)
    margin: 60px 20px 20px 20px
  .step5--title
    font-weight: 700
    font-size: 20px
    line-height: 130%
    color: #FFFFFF
    @media screen and (min-width: 1024px)
      margin-bottom: 20px
    @media screen and (max-width: 1023px)
      margin-bottom: 27px
  .step5--subtitle
    font-weight: 500
    font-size: 14px
    line-height: 130%
    color: #DCE7F2
    margin-bottom: 51px
    .step5--subtitle-green
      margin: 0 5px
      color: #5fefc0 !important
      cursor: pointer
  .step5--btn
    margin-bottom: 20px
    width: 100%
  .checkboxTitle
    font-weight: 500
    font-size: 14px
    line-height: 130%
    color: #9D9DD5
    display: flex
    flex-direction: row
    flex-wrap: wrap
    .checkboxText
      margin-right: 5px
    .checkboxLink
      cursor: pointer
      text-decoration: underline
      color: white !important
      margin-right: 5px
  .submitContainer
    display: flex
    justify-content: center
    width: 100%
    margin: 15px 0
    .subtmitBtn
      box-shadow: 0px 15px 40px rgba(95, 239, 192, 0.18)
      .label
        font-size: 14px !important
  .close-btn
    cursor: pointer
    position: absolute
    top: 25px
    right: 15px
  .activeStep
    background: #5FEFC0
  .inactiveStep
    background: #5C5C73
  .steps
    position: absolute
    top: 0px
    left: 0px
    width: 100%
    display: grid
    grid-gap: 2px
    grid-template-columns: 1fr 1fr 1fr
    z-index: 120
    .firstStep
      border-top-left-radius: 10px
    .step
      width: 100%
      height: 10px
      margin-right: 2px
    .lastStep
      border-top-right-radius: 10px
      margin-right: 0px !important
  .contentContainer
    @media screen and (max-width: 1023px)
      max-height: calc(100vh - 60px)
      overflow: auto
      margin-top: 10px
      padding: 15px 20px 15px 20px
    .mainTitle
      font-weight: 700
      font-size: 24px
      line-height: 130%
      text-align: center
      color: #FFFFFF
      width: 100%
      margin: 15px 0
    .title, .subtitle
      font-weight: 500
      font-size: 16px
      line-height: 130%
      text-align: justify
      color: #FFFFFF
    .title
      margin: 34px 0 10px
    .subtitle
      margin: 0 0 10px 0
  .btnsContainer
    min-height: 50px
    display: flex
    flex-direction: row
    justify-content: space-between

    .next-btn
      width: 89px
      .label
        font-size: 14px !important
</style>
